import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { AppShell, useShell } from '../components/AppShell';
import { OnboardingBanner } from '../components/OnboardingBanner';
import { PaymentActionSheet } from '../components/PaymentActionSheet';
import { greeting, owingFor, DAY_NAMES, MONTHS } from '../lib/dashboard/helpers';
import { loadDashboardData } from '../lib/dashboard/loadDashboardData';
import { KpiGrid } from '../components/dashboard/KpiGrid';
import { TodayChair } from '../components/dashboard/TodayChair';
import { WeekBars, ChannelSplit } from '../components/dashboard/Charts';
import { BarberDashboard } from '../components/dashboard/BarberDashboard';
import { SmsUsageCard } from '../components/dashboard/SmsUsageCard';
import { SmsStatusCard } from '../components/dashboard/SmsStatusCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardInner = () => {
  const { profile } = useShell();
  const [loading, setLoading] = useState(true);
  const [todayList, setTodayList] = useState([]);
  const [kpis, setKpis] = useState({
    today: { v: 0, delta: 0 },
    week: { v: 0, deltaPct: 0 },
    revenue: { v: 0, cash: 0, card: 0 },
    outstanding: { v: 0 },
    deposits: { v: 0, count: 0 },
    noShow: { v: 0, deltaPts: 0 },
  });
  const [weekBars, setWeekBars] = useState(Array(7).fill(0));
  const [channels, setChannels] = useState({ web: 0, manual: 0, rebook: 0, webPct: 0, manualPct: 0, rebookPct: 0 });
  const [busyId, setBusyId] = useState(null);
  // Payment-collection sheet (shown when a booking is being marked
  // complete AND has outstanding balance). Holds the booking we're
  // acting on so the sheet can render context.
  const [payFor, setPayFor] = useState(null);

  // If the current user is also linked to a barber row, we surface
  // their personal earnings alongside the shop-wide one. `null` = pure
  // shop_admin (no personal card). Set once on mount.
  const [myBarberId, setMyBarberId] = useState(null);
  // "Today's chair" scope toggle. Session-only. Only relevant when the
  // signed-in shop_admin is ALSO a barber in this shop.
  const [todayScope, setTodayScope] = useState('shop'); // 'shop' | 'mine'
  // Per-barber capabilities loaded alongside myBarberId.
  const [myPerms, setMyPerms] = useState({ canManageBookings: null, canTakePayments: null });
  // Collapsible "Shop overview" panel. Defaults open.
  const [overviewOpen, setOverviewOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sess } = await supabase.auth.getUser();
      if (!sess?.user) return;
      const { data } = await supabase
        .from('barbers').select('id,can_manage_bookings,can_take_payments')
        .eq('user_id', sess.user.id).maybeSingle();
      if (!cancelled) {
        setMyBarberId(data?.id || null);
        setMyPerms({
          canManageBookings: data ? !!data.can_manage_bookings : null,
          canTakePayments: data ? !!data.can_take_payments : null,
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fires PATCH /api/bookings/{id}/status with the caller's JWT and
  // optimistically updates the local list. Shop admins + barbers both
  // land here — RLS on the backend gates whether the update is allowed.
  //
  // Audit addition: when the target status is 'completed' AND there's
  // an outstanding balance, we route through the Cash/Card sheet so
  // the barber can record how it was collected.
  const updateStatus = async (bookingId, status) => {
    if (status === 'completed') {
      const booking = todayList.find((b) => b.id === bookingId);
      if (booking && owingFor(booking) > 0) {
        setPayFor(booking);
        return;
      }
    }
    await patchStatus(bookingId, status);
  };

  const patchStatus = async (bookingId, status) => {
    setBusyId(bookingId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Update failed (${res.status}): ${t}`);
      }
      setTodayList((prev) => prev.map((b) => b.id === bookingId ? { ...b, status } : b));
    } catch (e) {
      alert(e.message || 'Failed to update booking.');
    } finally {
      setBusyId(null);
    }
  };

  const recordBalanceAndComplete = async (method) => {
    if (!payFor) return;
    const bookingId = payFor.id;
    const amount = owingFor(payFor);
    setBusyId(bookingId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const payRes = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ balance_paid_method: method, balance_paid_amount: amount }),
      });
      if (!payRes.ok) throw new Error(`Balance record failed (${payRes.status})`);
      await patchStatus(bookingId, 'completed');
      setTodayList((prev) => prev.map((b) => b.id === bookingId
        ? { ...b, status: 'completed', paid_in_full: true, deposit_paid: true, payment_status: 'succeeded', balance_paid_method: method, balance_paid_amount: amount }
        : b));
    } catch (e) {
      alert(e.message || 'Failed to record payment.');
    } finally {
      setBusyId(null);
      setPayFor(null);
    }
  };

  const shop = profile?.shops;
  const isBarber = profile?.role === 'barber';

  // Sequence guard for loadAll — a booking action can trigger multiple
  // refetch signals (the hook's `onChanged` prop callback + the window
  // `instacut:booking-changed` event, sometimes for both a payment
  // write AND a status write). We debounce those triggers into one
  // call, and tag each in-flight loadAll with a sequence number so a
  // slow earlier response can never overwrite the newer state.
  const loadSeqRef = useRef(0);
  const refetchTimerRef = useRef(null);

  useEffect(() => {
    if (!profile?.shop_id) return;
    loadAll(profile.shop_id);

    // Keep KPIs fresh without forcing a browser refresh:
    //   • window `focus` / tab `visibilitychange` — barber flips back
    //     from the Diary tab after creating a booking
    //   • custom `instacut:booking-changed` — dispatched by
    //     `useBookingActions` and the new-booking wizard after any
    //     successful mutation so this page reloads even when the two
    //     surfaces are open in the same tab (nav via router).
    // All three funnel through a 120ms-debounced refetcher.
    const scheduleRefetch = () => {
      if (!profile?.shop_id) return;
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      refetchTimerRef.current = setTimeout(() => {
        refetchTimerRef.current = null;
        loadAll(profile.shop_id);
      }, 120);
    };
    const onVis = () => { if (document.visibilityState === 'visible') scheduleRefetch(); };
    window.addEventListener('focus', scheduleRefetch);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('instacut:booking-changed', scheduleRefetch);
    return () => {
      window.removeEventListener('focus', scheduleRefetch);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('instacut:booking-changed', scheduleRefetch);
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    };
  }, [profile?.shop_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async (shopId) => {
    // Bump sequence — any earlier in-flight loadAll whose seq is now
    // stale will bail out at the setState guard below.
    const seq = ++loadSeqRef.current;
    setLoading(true);
    const data = await loadDashboardData(shopId);
    if (seq !== loadSeqRef.current) return;
    setKpis(data.kpis);
    setTodayList(data.todayList);
    setWeekBars(data.weekBars);
    setChannels(data.channels);
    setLoading(false);
  };

  const ownerName = profile?.full_name?.split(' ')[0] || 'there';
  const now = new Date();
  const subtitle = `${shop?.name || 'Your shop'} · ${DAY_NAMES[now.getDay()]} ${now.getDate()} ${MONTHS[now.getMonth()]}`;

  // Barber view branches here — after all hooks have run.
  if (isBarber) {
    return (
      <BarberDashboard
        profile={profile}
        shop={shop}
        myBarberId={myBarberId}
        myPerms={myPerms}
        todayList={todayList.filter((b) => b.barber?.id === myBarberId)}
        loading={loading}
        reload={() => loadAll(shop?.id)}
      />
    );
  }

  return (
    <main className="main">
      <div className="topbar" style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <h2 data-testid="dashboard-greeting">{greeting(now.getHours())}, {ownerName}</h2>
          <div className="sub">{subtitle}</div>
        </div>
        <div className="av" style={{ flexShrink: 0, marginLeft: 12 }}>{(profile?.full_name || 'M').charAt(0).toUpperCase()}</div>
      </div>

      <div className="body" data-testid="dashboard-body">
        <OnboardingBanner profile={profile} />

        <SmsUsageCard />
        <SmsStatusCard />

        <KpiGrid
          kpis={kpis}
          shopId={profile?.shop_id}
          myBarberId={myBarberId}
          open={overviewOpen}
          onToggle={() => setOverviewOpen((v) => !v)}
        />

        <div className="cols">
          <TodayChair
            todayList={todayList}
            loading={loading}
            busyId={busyId}
            myBarberId={myBarberId}
            myPerms={myPerms}
            isBarber={isBarber}
            todayScope={todayScope}
            setTodayScope={setTodayScope}
            updateStatus={updateStatus}
          />

          <div className="col-stack">
            <WeekBars weekBars={weekBars} />
            <ChannelSplit channels={channels} />
          </div>
        </div>
      </div>

      <PaymentActionSheet
        active={!!payFor}
        outstanding={payFor ? owingFor(payFor) : 0}
        customerName={payFor?.customer?.name || '—'}
        serviceName={payFor?.service?.name || '—'}
        busy={busyId === payFor?.id}
        onPick={(m) => recordBalanceAndComplete(m)}
        onSkip={() => { const id = payFor?.id; setPayFor(null); if (id) patchStatus(id, 'completed'); }}
        onClose={() => setPayFor(null)}
      />
    </main>
  );
};

const Dashboard = () => (
  <AppShell>
    <DashboardInner />
  </AppShell>
);

export default Dashboard;
